import type { ApiErrorBody } from '@odr/shared';

const ACCESS_KEY = 'odr.accessToken';
const REFRESH_KEY = 'odr.refreshToken';

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details?: Array<{ path: string; message: string }>;
  /** True when the request never reached the server (offline, DNS, timeout). */
  readonly isNetworkError: boolean;

  constructor(
    status: number,
    code: string,
    message: string,
    options: {
      details?: Array<{ path: string; message: string }>;
      isNetworkError?: boolean;
    } = {},
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = options.details;
    this.isNetworkError = options.isNetworkError ?? false;
  }
}

export const tokens = {
  access: () => localStorage.getItem(ACCESS_KEY),
  refresh: () => localStorage.getItem(REFRESH_KEY),
  set(access: string, refresh: string) {
    localStorage.setItem(ACCESS_KEY, access);
    localStorage.setItem(REFRESH_KEY, refresh);
  },
  clear() {
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
  },
};

type Listener = () => void;
const unauthorizedListeners = new Set<Listener>();

/** Fired when the session is gone for good, so the app can route to sign-in. */
export function onUnauthorized(listener: Listener): () => void {
  unauthorizedListeners.add(listener);
  return () => unauthorizedListeners.delete(listener);
}

/**
 * Single-flight refresh.
 *
 * why: a screen typically fires several requests at once. Without this, an
 * expired token produces N parallel refreshes, and rotation means all but one
 * present an already-consumed refresh token — which the server treats as reuse
 * and revokes the whole session.
 */
let refreshInFlight: Promise<boolean> | null = null;

async function refreshSession(): Promise<boolean> {
  refreshInFlight ??= (async () => {
    const refreshToken = tokens.refresh();
    if (!refreshToken) return false;
    try {
      const response = await fetch('/api/auth/refresh', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });
      if (!response.ok) return false;
      const body = (await response.json()) as { accessToken: string; refreshToken: string };
      tokens.set(body.accessToken, body.refreshToken);
      return true;
    } catch {
      return false;
    } finally {
      // Release the latch on the next tick so concurrent callers all observe
      // the same outcome before a new attempt can start.
      setTimeout(() => {
        refreshInFlight = null;
      }, 0);
    }
  })();
  return refreshInFlight;
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  /** FormData for uploads — skips JSON encoding. */
  form?: FormData;
  signal?: AbortSignal;
  /** Skip the automatic refresh-and-retry (used by auth calls themselves). */
  noRetry?: boolean;
}

async function parseError(response: Response): Promise<ApiError> {
  let code = 'REQUEST_FAILED';
  let message = 'Something went wrong. Please try again.';
  let details: Array<{ path: string; message: string }> | undefined;

  try {
    const body = (await response.json()) as ApiErrorBody;
    if (body?.error) {
      code = body.error.code ?? code;
      message = body.error.message ?? message;
      details = body.error.details;
    }
  } catch {
    // Non-JSON error body (proxy error page, gateway timeout) — keep the default.
  }

  return new ApiError(response.status, code, message, { details });
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const send = async (): Promise<Response> => {
    const headers: Record<string, string> = {};
    const access = tokens.access();
    if (access) headers.Authorization = `Bearer ${access}`;
    if (!options.form && options.body !== undefined) {
      headers['Content-Type'] = 'application/json';
    }

    return fetch(path, {
      method: options.method ?? (options.body || options.form ? 'POST' : 'GET'),
      headers,
      body: options.form ?? (options.body === undefined ? undefined : JSON.stringify(options.body)),
      signal: options.signal,
    });
  };

  let response: Response;
  try {
    response = await send();
  } catch (error) {
    if ((error as Error)?.name === 'AbortError') throw error;
    throw new ApiError(
      0,
      'OFFLINE',
      'No connection to the ODR server. Your work is saved on this device.',
      { isNetworkError: true },
    );
  }

  if (response.status === 401 && !options.noRetry) {
    const refreshed = await refreshSession();
    if (refreshed) {
      response = await send();
    } else {
      tokens.clear();
      for (const listener of unauthorizedListeners) listener();
      throw new ApiError(401, 'UNAUTHORIZED', 'Your session has ended. Please sign in again.');
    }
  }

  if (!response.ok) throw await parseError(response);

  if (response.status === 204) return undefined as T;

  const contentType = response.headers.get('content-type') ?? '';
  if (!contentType.includes('application/json')) return (await response.text()) as T;
  return (await response.json()) as T;
}

export const api = {
  get: <T>(path: string, signal?: AbortSignal) => request<T>(path, { method: 'GET', signal }),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: 'POST', body }),
  put: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PUT', body }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PATCH', body }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
  upload: <T>(path: string, form: FormData) => request<T>(path, { method: 'POST', form }),
};

/** Build a query string, dropping empty values so URLs stay readable. */
export function qs(params: Record<string, unknown>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    if (Array.isArray(value)) {
      if (value.length === 0) continue;
      search.set(key, value.join(','));
    } else {
      search.set(key, String(value));
    }
  }
  const s = search.toString();
  return s ? `?${s}` : '';
}
